import Phaser from 'phaser'
import { GAME_WIDTH, GAME_HEIGHT } from '../../config'
import { BaseMiniGameScene, type MiniGameConfig } from './BaseMiniGameScene'

interface Strawberry {
  sprite: Phaser.GameObjects.Image
  guideLine: Phaser.GameObjects.Line
  leftHalf?: Phaser.GameObjects.Image
  rightHalf?: Phaser.GameObjects.Image
  isCut: boolean
  guideStartX: number
  guideStartY: number
  guideEndX: number
  guideEndY: number
}

/**
 * Chopping Mini-Game
 * Slice strawberries with swipe gestures along guide lines
 */
export class ChoppingMiniGameScene extends BaseMiniGameScene {
  private cuttingBoard!: Phaser.GameObjects.Image
  private currentStrawberry?: Strawberry
  private strawberriesCut: number = 0
  private totalStrawberries: number = 8
  private swipeStartPoint?: Phaser.Math.Vector2
  private swipeLine?: Phaser.GameObjects.Line
  private isProcessing: boolean = false

  constructor() {
    super({ key: 'ChoppingMiniGameScene' })
  }

  getConfig(): MiniGameConfig {
    return {
      maxScore: this.totalStrawberries * 100, // 100 points per perfect cut
      totalItems: this.totalStrawberries,
    }
  }

  getNextScene(): string | undefined {
    return 'StirringMiniGameScene' // Next step in recipe
  }

  protected createBackground(): void {
    // Kitchen-themed gradient background
    const graphics = this.add.graphics()

    // Warm kitchen colors
    const topColor = Phaser.Display.Color.HexStringToColor('#fff5e6')
    const bottomColor = Phaser.Display.Color.HexStringToColor('#ffe4c4')

    for (let y = 0; y < GAME_HEIGHT; y++) {
      const ratio = y / GAME_HEIGHT
      const color = Phaser.Display.Color.Interpolate.ColorWithColor(
        topColor,
        bottomColor,
        100,
        ratio * 100
      )
      graphics.fillStyle(Phaser.Display.Color.GetColor(color.r, color.g, color.b))
      graphics.fillRect(0, y, GAME_WIDTH, 1)
    }
  }

  setupGame(): void {
    this.strawberriesCut = 0
    this.isProcessing = false

    this.createCuttingBoard()
    this.createInstructions()
    this.setupInput()
    this.spawnStrawberry()
  }

  private createCuttingBoard(): void {
    // Use placeholder texture
    this.cuttingBoard = this.add.image(GAME_WIDTH / 2, GAME_HEIGHT / 2 + 50, 'cutting_board')
    this.cuttingBoard.setScale(1.5)
  }

  private createInstructions(): void {
    const instructionText = this.add.text(
      GAME_WIDTH / 2,
      GAME_HEIGHT - 60,
      '✋ Swipe along the line to cut!',
      {
        fontFamily: 'system-ui',
        fontSize: '18px',
        color: '#8b4513',
      }
    )
    instructionText.setOrigin(0.5)
    instructionText.setAlpha(0.8)
  }

  private setupInput(): void {
    this.input.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
      if (this.isProcessing || this.isComplete) return

      this.swipeStartPoint = new Phaser.Math.Vector2(pointer.x, pointer.y)

      // Create visual swipe line
      this.swipeLine = this.add.line(
        0, 0,
        pointer.x, pointer.y,
        pointer.x, pointer.y,
        0xffffff
      )
      this.swipeLine.setLineWidth(4)
      this.swipeLine.setOrigin(0, 0)
    })

    this.input.on('pointermove', (pointer: Phaser.Input.Pointer) => {
      if (!this.swipeStartPoint || !this.swipeLine) return

      // Update swipe line visual
      this.swipeLine.setTo(
        this.swipeStartPoint.x,
        this.swipeStartPoint.y,
        pointer.x,
        pointer.y
      )
    })

    this.input.on('pointerup', (pointer: Phaser.Input.Pointer) => {
      if (!this.swipeStartPoint || this.isProcessing || this.isComplete) {
        this.cleanupSwipe()
        return
      }

      const swipeEnd = new Phaser.Math.Vector2(pointer.x, pointer.y)
      this.evaluateSwipe(this.swipeStartPoint, swipeEnd)
      this.cleanupSwipe()
    })
  }

  private cleanupSwipe(): void {
    this.swipeStartPoint = undefined
    if (this.swipeLine) {
      this.swipeLine.destroy()
      this.swipeLine = undefined
    }
  }

  private spawnStrawberry(): void {
    if (this.strawberriesCut >= this.totalStrawberries) {
      return
    }

    // Randomize strawberry position slightly
    const centerX = GAME_WIDTH / 2 + Phaser.Math.Between(-30, 30)
    const centerY = GAME_HEIGHT / 2 + 30 + Phaser.Math.Between(-20, 20)

    // Create strawberry sprite
    const sprite = this.add.image(centerX, centerY, 'strawberry')
    sprite.setScale(1.2)

    // Create guide line (horizontal cut through middle)
    const lineOffset = 50 * sprite.scaleX
    const guideStartX = centerX - lineOffset
    const guideStartY = centerY
    const guideEndX = centerX + lineOffset
    const guideEndY = centerY

    const guideLine = this.add.line(
      0, 0,
      guideStartX, guideStartY,
      guideEndX, guideEndY,
      0xff6b6b
    )
    guideLine.setLineWidth(3)
    guideLine.setOrigin(0, 0)
    guideLine.setAlpha(0.8)

    // Animate guide line to draw attention
    this.tweens.add({
      targets: guideLine,
      alpha: 0.4,
      duration: 500,
      yoyo: true,
      repeat: -1,
    })

    this.currentStrawberry = {
      sprite,
      guideLine,
      isCut: false,
      guideStartX,
      guideStartY,
      guideEndX,
      guideEndY,
    }

    // Entrance animation
    sprite.setScale(0)
    guideLine.setAlpha(0)

    this.tweens.add({
      targets: sprite,
      scale: 1.2,
      duration: 300,
      ease: 'Back.easeOut',
    })

    this.tweens.add({
      targets: guideLine,
      alpha: 0.8,
      duration: 300,
      delay: 200,
    })
  }

  private evaluateSwipe(start: Phaser.Math.Vector2, end: Phaser.Math.Vector2): void {
    if (!this.currentStrawberry || this.currentStrawberry.isCut) return

    const strawberry = this.currentStrawberry

    // Check if swipe is long enough
    const swipeLength = Phaser.Math.Distance.Between(start.x, start.y, end.x, end.y)
    if (swipeLength < 50) return

    // Check if swipe passes through/near the guide line
    const accuracy = this.calculateCutAccuracy(start, end, strawberry)

    if (accuracy > 0) {
      this.performCut(accuracy)
    }
  }

  private calculateCutAccuracy(
    swipeStart: Phaser.Math.Vector2,
    swipeEnd: Phaser.Math.Vector2,
    strawberry: Strawberry
  ): number {
    // Check if swipe line intersects or is close to guide line
    const guideStart = new Phaser.Math.Vector2(strawberry.guideStartX, strawberry.guideStartY)
    const guideEnd = new Phaser.Math.Vector2(strawberry.guideEndX, strawberry.guideEndY)

    // Check for intersection
    const intersection = Phaser.Geom.Intersects.LineToLine(
      new Phaser.Geom.Line(swipeStart.x, swipeStart.y, swipeEnd.x, swipeEnd.y),
      new Phaser.Geom.Line(guideStart.x, guideStart.y, guideEnd.x, guideEnd.y)
    )

    if (!intersection) {
      // Check if swipe passes near the strawberry at least
      const strawberryCenter = new Phaser.Math.Vector2(
        strawberry.sprite.x,
        strawberry.sprite.y
      )

      const distToStart = Phaser.Math.Distance.BetweenPoints(strawberryCenter, swipeStart)
      const distToEnd = Phaser.Math.Distance.BetweenPoints(strawberryCenter, swipeEnd)

      // If neither end is near the strawberry, no cut
      if (distToStart > 100 && distToEnd > 100) return 0

      // Partial credit for cutting near but not on the line
      return 0.5
    }

    // Calculate how parallel the swipe is to the guide line
    const swipeAngle = Phaser.Math.Angle.BetweenPoints(swipeStart, swipeEnd)
    const guideAngle = Phaser.Math.Angle.BetweenPoints(guideStart, guideEnd)
    const angleDiff = Math.abs(Phaser.Math.Angle.Wrap(swipeAngle - guideAngle))

    // Normalize angle difference (0 = parallel, PI/2 = perpendicular)
    const normalizedAngle = Math.min(angleDiff, Math.PI - angleDiff)

    // Perfect cut: nearly horizontal (matching guide)
    if (normalizedAngle < 0.3) return 1.0 // Perfect
    if (normalizedAngle < 0.6) return 0.75 // Good
    return 0.5 // Okay
  }

  private performCut(accuracy: number): void {
    if (!this.currentStrawberry) return

    this.isProcessing = true
    this.currentStrawberry.isCut = true

    const strawberry = this.currentStrawberry
    const centerX = strawberry.sprite.x
    const centerY = strawberry.sprite.y

    // Determine score and feedback
    let points: number
    let feedbackText: string
    let feedbackColor: number

    if (accuracy >= 1.0) {
      points = 100
      feedbackText = 'Perfect!'
      feedbackColor = 0xffd700
      this.cameras.main.shake(100, 0.005)
    } else if (accuracy >= 0.75) {
      points = 75
      feedbackText = 'Great!'
      feedbackColor = 0x4caf50
    } else {
      points = 50
      feedbackText = 'OK'
      feedbackColor = 0x2196f3
    }

    // Add score
    this.addScore(points, centerX, centerY - 50)
    this.showFeedback(feedbackText, centerX, centerY - 80, feedbackColor)

    // Hide original strawberry
    strawberry.sprite.setVisible(false)
    strawberry.guideLine.destroy()

    // Create cut halves
    const leftHalf = this.add.image(centerX - 15, centerY, 'strawberry_half_left')
    const rightHalf = this.add.image(centerX + 15, centerY, 'strawberry_half_right')
    leftHalf.setScale(1.2)
    rightHalf.setScale(1.2)

    // Animate halves splitting apart
    this.tweens.add({
      targets: leftHalf,
      x: centerX - 60,
      y: centerY + 100,
      angle: -30,
      alpha: 0,
      duration: 600,
      ease: 'Power2',
    })

    this.tweens.add({
      targets: rightHalf,
      x: centerX + 60,
      y: centerY + 100,
      angle: 30,
      alpha: 0,
      duration: 600,
      ease: 'Power2',
    })

    // Juice splatter particles
    this.createJuiceSplatter(centerX, centerY)

    // Update progress
    this.strawberriesCut++
    this.incrementProgress()

    // Spawn next strawberry after delay
    this.time.delayedCall(800, () => {
      this.isProcessing = false

      // Clean up current strawberry
      if (this.currentStrawberry) {
        this.currentStrawberry.sprite.destroy()
        leftHalf.destroy()
        rightHalf.destroy()
      }
      this.currentStrawberry = undefined

      // Spawn next if not complete
      if (!this.isComplete) {
        this.spawnStrawberry()
      }
    })
  }

  private createJuiceSplatter(x: number, y: number): void {
    // Create particle effect for juice
    const colors = [0xff6b6b, 0xff8888, 0xffaaaa]

    for (let i = 0; i < 12; i++) {
      const particle = this.add.circle(
        x,
        y,
        Phaser.Math.Between(3, 8),
        Phaser.Utils.Array.GetRandom(colors)
      )

      const angle = Phaser.Math.FloatBetween(0, Math.PI * 2)
      const distance = Phaser.Math.Between(30, 80)
      const targetX = x + Math.cos(angle) * distance
      const targetY = y + Math.sin(angle) * distance

      this.tweens.add({
        targets: particle,
        x: targetX,
        y: targetY,
        alpha: 0,
        scale: 0,
        duration: Phaser.Math.Between(400, 700),
        ease: 'Power2',
        onComplete: () => particle.destroy(),
      })
    }
  }
}
